import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import './FaseBadge.css';

/**
 * A fase do vendedor, para aparecer em toda tela que lista ou seleciona vendedor.
 *
 * UM HOOK EM LOTE, NÃO UMA CHAMADA POR LINHA: `useFasesDosVendedores` busca todas as fases de uma
 * vez. Um badge que buscasse a própria fase faria 174 requisições numa árvore de vendedores — e
 * pior, o número cresceria em silêncio conforme o cliente crescesse.
 *
 * O badge é deliberadamente pequeno e informativo:
 *   - fase e posição no programa ("2. Nivelamento · 3 de 4");
 *   - dia X de N dentro da fase;
 *   - marca de PLACAR TRAVADO, que é o que explica ao CS por que aquele vendedor não está no
 *     ranking — sem isso a exclusão parece bug, e a pergunta chega ao suporte.
 * Vendedor fora de fase não renderiza nada: o ongoing é a maioria e poluir a tela com "sem fase"
 * em 90% das linhas tira a atenção justamente de quem está em programa.
 */

/** Busca em lote as fases vigentes. Passe `salesmanIds` (array) OU `storeId`, ou nenhum para todos. */
export function useFasesDosVendedores({ subdomain, salesmanIds, storeId, atDate } = {}) {
    const [fases, setFases] = useState({});
    const [carregando, setCarregando] = useState(false);
    const chave = JSON.stringify([subdomain, salesmanIds, storeId, atDate]);

    useEffect(() => {
        if (!subdomain) { setFases({}); return; }
        let vivo = true;
        setCarregando(true);
        (async () => {
            try {
                const r = await apiService.phaseResolveBatch({
                    subdomain,
                    salesman_ids: salesmanIds && salesmanIds.length ? salesmanIds : undefined,
                    store_id: storeId || undefined,
                    at_date: atDate || undefined,
                });
                if (vivo) setFases(r?.data?.phases || {});
            } catch (e) {
                // Falha aqui não pode derrubar a tela: fase é informação adicional, e a tela
                // funcionava sem ela antes.
                if (vivo) setFases({});
            } finally {
                if (vivo) setCarregando(false);
            }
        })();
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chave]);

    return { fases, carregando };
}

/** `fase` é o objeto vindo do hook: `fases[salesman_id]`. */
/**
 * `mostrarDia=false` esconde o "· dia 5/14". Existe por causa da TABELA de Feedbacks /
 * Atendimentos: ali a coluna Vendedor concorre com outras 12 colunas, e o nome da fase é o que
 * responde a pergunta da tela ("por que este atendimento pontuou por outra régua"). O dia continua
 * no `title`, para quem passar o mouse.
 */
export function FaseBadge({ fase, compacto = false, mostrarDia = true }) {
    if (!fase) return null;
    const titulo = [
        `${fase.program?.name} · turma ${fase.cohort?.name}`,
        `Fase ${fase.sort_order} de ${fase.phases_total}: ${fase.phase_name}`,
        fase.day_of_phase && fase.duration_days ? `Dia ${fase.day_of_phase} de ${fase.duration_days}` : null,
        fase.locks_scoreboard
            ? 'Nesta fase o placar individual fecha e o vendedor sai do ranking da loja.'
            : 'Esta fase não trava o placar: o vendedor segue no ranking.',
    ].filter(Boolean).join('\n');

    return (
        <span className={`fase-badge ${fase.locks_scoreboard ? 'fase-badge-trava' : ''}`} title={titulo}>
            {compacto
                ? `${fase.sort_order}/${fase.phases_total}`
                : `${fase.sort_order}. ${fase.phase_name}`}
            {!compacto && mostrarDia && fase.day_of_phase && fase.duration_days && (
                <em> · dia {fase.day_of_phase}/{fase.duration_days}</em>
            )}
            {fase.locks_scoreboard && <span className="fase-badge-lock" aria-label="placar travado">🔒</span>}
        </span>
    );
}

/**
 * Versão TEXTO da fase, para onde não cabe componente: `<option>` de `<select>`, `title`, confirmação.
 * HTML não permite elemento dentro de `<option>`, então tentar usar o badge ali renderiza nada.
 */
export function faseTexto(fase, { comDia = true } = {}) {
    if (!fase) return '';
    const dia = comDia && fase.day_of_phase && fase.duration_days
        ? ` dia ${fase.day_of_phase}/${fase.duration_days}`
        : '';
    return ` · ${fase.sort_order}. ${fase.phase_name}${dia}${fase.locks_scoreboard ? ' 🔒' : ''}`;
}

export default FaseBadge;
